<div data-testid="inbound-notice-edit-page" class="inbound-form-wrapper-container">
  <!-- Sticky Header -->
  <div class="sticky-header">
    <div class="flex justify-between items-center mb-5">
      <h2 class="text-2xl font-semibold text-gray-800">
        {{ "inbound.notice.title.edit" | translate }}
      </h2>
      <div class="flex gap-3">
        <app-button
          [testId]="'inbound-notice-edit-cancel-btn'"
          [label]="'common.actions.cancel' | translate"
          (emitOnClick)="onCancel()"
          [outlined]="true"
          type="secondary"
          [disabled]="saving()"
        ></app-button>
        <app-button
          [testId]="'inbound-notice-edit-save-btn'"
          [label]="'common.actions.save' | translate"
          (emitOnClick)="onSave()"
          type="info"
          [loading]="saving()"
          [disabled]="saving() || loading()"
        ></app-button>
      </div>
    </div>
  </div>

  <!-- Content -->
  <div class="inbound-form-wrapper-content">
    <div class="p-2">
      @if (loading()) {
        <div class="flex justify-center items-center py-8">
          <p-progressSpinner data-testid="inbound-notice-edit-loading"></p-progressSpinner>
        </div>
      } @else {
        <!-- Basic Information Section (readonly) -->
        <section class="form-section mb-4 notice-basic-section">
          <div class="flex items-center mb-2 px-4">
            <h2 class="section-title text-lg font-semibold mr-4">
              {{ "inbound.sections.basic_information" | translate }}
            </h2>
          </div>
          <div class="grid gap-2 pl-9 pr-4 py-2 grid-cols-4">
            <!-- code, innerOrderNo, creationDate, status, warehouseName -->
            <div class="form-field">
              <span class="form-label text-sm">
                {{ "inbound.notice.fields.notice_code" | translate }}
              </span>
              <p class="text-sm text-gray-800 mt-1">
                {{ getFieldValue("code") || "-" }}
              </p>
            </div>
            <div class="form-field">
              <span class="form-label text-sm">
                {{ "inbound.table.columns.inner_order_number" | translate }}
              </span>
              <p class="text-sm text-gray-800 mt-1">
                {{ getFieldValue("innerOrderNo") || "-" }}
              </p>
            </div>
            <div class="form-field">
              <span class="form-label text-sm">
                {{ "inbound.notice.fields.creation_date" | translate }}
              </span>
              <p class="text-sm text-gray-800 mt-1">
                {{ formatDate(getFieldValue("creationDate")) || "-" }}
              </p>
            </div>
            <div class="form-field">
              <span class="form-label text-sm">
                {{ "inbound.notice.fields.status" | translate }}
              </span>
              <div class="mt-1">
                <p-tag
                  data-testid="inbound-notice-edit-status-tag"
                  [value]="getStatusLabel(getFieldValue('status'))"
                  [severity]="getStatusSeverity(getFieldValue('status'))"
                  styleClass="status-tag"
                  [ngStyle]="getStatusStyle(getFieldValue('status'))"
                  pTooltip="{{ getStatusLabel(getFieldValue('status')) }}"
                  tooltipPosition="top"
                  tooltipStyleClass="custom-tooltip"
                  [showDelay]="300"
                  [hideDelay]="0"
                />
              </div>
            </div>
            <div class="form-field">
              <span class="form-label text-sm">
                {{ "inbound.notice.fields.warehouse_name" | translate }}
              </span>
              <p class="text-sm text-gray-800 mt-1">
                {{ getFieldValue("warehouseName") || "-" }}
              </p>
            </div>
          </div>
        </section>

        <!-- Editable Information Section -->
        <section class="form-section mb-4 notice-basic-section">
          <div class="flex items-center mb-2 px-4">
            <h2 class="section-title text-lg font-semibold mr-4">
              {{ "inbound.notice.sections.editable_information" | translate }}
            </h2>
          </div>
          <div class="grid gap-2 pl-9 pr-4 py-2 grid-cols-4">
            <!-- realNoticeCode, trackingNumber, carrierName -->
            <div class="form-field">
              <label for="ine-real-notice-code" class="form-label text-sm">
                {{ "inbound.notice.fields.real_notice_code" | translate }}
              </label>
              <input
                data-testid="inbound-notice-edit-real-notice-code-input"
                pInputText
                id="ine-real-notice-code"
                [formControl]="getFormControl('realNoticeCode')"
                [placeholder]="
                  'inbound.notice.fields.real_notice_code' | translate
                "
                class="w-full text-sm"
              />
            </div>
            <div class="form-field">
              <label for="ine-tracking-number" class="form-label text-sm">
                {{ "inbound.notice.fields.tracking_number" | translate }}
              </label>
              <input
                data-testid="inbound-notice-edit-tracking-number-input"
                pInputText
                id="ine-tracking-number"
                [formControl]="getFormControl('trackingNumber')"
                [placeholder]="
                  'inbound.notice.fields.tracking_number' | translate
                "
                class="w-full text-sm"
              />
            </div>
            <div class="form-field">
              <label for="ine-carrier-name" class="form-label text-sm">
                {{ "inbound.notice.fields.carrier_name" | translate }}
              </label>
              <input
                data-testid="inbound-notice-edit-carrier-name-input"
                pInputText
                id="ine-carrier-name"
                [formControl]="getFormControl('carrierName')"
                [placeholder]="'inbound.notice.fields.carrier_name' | translate"
                class="w-full text-sm"
              />
            </div>
          </div>
          <div class="grid gap-2 pl-9 pr-4 py-2 grid-cols-4">
            <!-- deliveryDate, remarks -->
            <div class="form-field">
              <span class="form-label text-sm">
                {{ "inbound.notice.fields.delivery_date" | translate }}
              </span>
              <app-advanced-datepicker
                data-testid="inbound-notice-edit-delivery-date-input"
                [formControl]="getFormControl('deliveryDate')"
                [placeholder]="
                  'inbound.notice.fields.delivery_date' | translate
                "
                class="w-full text-sm datepicker-max-width"
              ></app-advanced-datepicker>
            </div>
            <div class="form-field">
              <label for="ine-remarks" class="form-label text-sm">
                {{ "inbound.notice.fields.remarks" | translate }}
              </label>
              <textarea
                data-testid="inbound-notice-edit-remarks-input"
                pInputTextarea
                id="ine-remarks"
                [formControl]="getFormControl('remarks')"
                [placeholder]="'inbound.notice.fields.remarks' | translate"
                [rows]="3"
                class="w-full text-sm"
              ></textarea>
            </div>
          </div>
        </section>

        <!-- Item Details Section -->
        <section class="form-section mb-4">
          <div class="flex items-center mb-2 px-4">
            <h2 class="section-title text-lg font-semibold mr-4">
              {{ "inbound.sections.item_details" | translate }}
            </h2>
          </div>
          <div class="pl-6 ml-4 py-1">
            <app-data-table
              data-testid="inbound-notice-edit-items-table"
              [columns]="itemTableColumns"
              [dataSource]="formattedItemDetails()"
              [loading]="loading()"
              [hasCheckColumn]="false"
              [totalRecords]="inboundDetailItemsForWrapper().length"
              [first]="0"
              [rows]="20"
            ></app-data-table>
          </div>
        </section>
      }
    </div>
  </div>
</div>

<!-- Toast Messages -->
<p-toast data-testid="inbound-notice-edit-toast"></p-toast>
